import React, { useMemo, useState } from "react";
import PageShell from "../dashboard/PageShell";

const SelectFundingCouncil: React.FC = () => {
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      // expected: topics/approval/detail/:id/council/funding → id at index length-3
      return parts[parts.length - 3] || "";
    } catch {
      return "";
    }
  }, []);

  const [form, setForm] = useState({
    councilId: "",
    decisionNumber: "",
    decisionSignedDate: "",
    meetingDate: "",
    appraisalResult: "",
    councilRecommendation: "",
    attachments: [] as File[],
  });

  const change = (field: string, value: string | File[]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      change("attachments", [...form.attachments, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    const updated = form.attachments.filter((_, i) => i !== index);
    change("attachments", updated);
  };

  return (
    <PageShell title="Chọn Hội đồng thẩm định kinh phí">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Chọn Hội đồng thẩm định kinh phí
          </h1>
        </div>

        {/* Topic header */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-800 font-medium">Tên đề tài:</div>
              <div className="text-gray-700">
                Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI
              </div>
            </div>
            <div>
              <div className="text-gray-800 font-medium">Chủ nhiệm:</div>
              <div className="text-gray-700">
                Thượng tá, VS. ThS Lê Anh Tuấn
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn hội đồng thẩm định
              </label>
              <select
                value={form.councilId}
                onChange={(e) => change("councilId", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">- Chọn hội đồng thẩm định -</option>
                <option value="1">Hội đồng 1</option>
                <option value="2">Hội đồng 2</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Số quyết định <span className="text-red-500">(*)</span>
              </label>
              <input
                type="text"
                value={form.decisionNumber}
                onChange={(e) => change("decisionNumber", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập số quyết định"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ngày ký QĐ TLHĐ <span className="text-red-500">(*)</span>
              </label>
              <input
                type="date"
                value={form.decisionSignedDate}
                onChange={(e) => change("decisionSignedDate", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ngày họp hội đồng <span className="text-red-500">(*)</span>
              </label>
              <input
                type="date"
                value={form.meetingDate}
                onChange={(e) => change("meetingDate", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kết quả thẩm định
              </label>
              <select
                value={form.appraisalResult}
                onChange={(e) => change("appraisalResult", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">- Chọn -</option>
                <option value="dat">Đạt</option>
                <option value="khong-dat">Không đạt</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nội dung kiến nghị, đề xuất của Hội đồng
              </label>
              <textarea
                value={form.councilRecommendation}
                onChange={(e) =>
                  change("councilRecommendation", e.target.value)
                }
                className="w-full min-h-[160px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File đính kèm
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                  id="funding-council-file-upload"
                />
                <label
                  htmlFor="funding-council-file-upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  Choose Files
                </label>
                <span className="text-sm text-gray-500">
                  {form.attachments.length === 0
                    ? "No file chosen"
                    : `${form.attachments.length} file(s) chosen`}
                </span>
              </div>

              {form.attachments.length > 0 && (
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto pr-1">
                  {form.attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                        title="Xóa file"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            onClick={() => window.history.back()}
          >
            Hủy
          </button>
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
            onClick={() => {
              console.log("Save funding council selection", form);
              const projectId = id || window.location.pathname.split("/")[4];
              window.location.href = `/topics/approval/detail/${projectId}`;
            }}
          >
            Lưu dữ liệu
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default SelectFundingCouncil;
